/**
 * useMediaQuery / useIsDesktop — live answers to "how wide is the screen?"
 *
 * Most responsive switching in this app is CSS-only (`hidden desktop:block`),
 * which renders BOTH variants and hides one. That's fine for static markup, but
 * not for filter controls: two live copies of the same <select> share ids,
 * labels and focus, and screen readers announce both. When a component needs
 * to render one variant OR the other, use this hook instead.
 *
 * Keep the queries in step with tailwind.config.js `screens` — DESKTOP_QUERY
 * matches the `desktop:` prefix (1024px), the first size that implies a mouse.
 *
 * `fallback` is what the hook returns where matchMedia doesn't exist (jsdom in
 * Jest). It defaults to true so tests see the full desktop layout, which is
 * what every pre-existing test was written against.
 */
import { useEffect, useState } from 'react';

/** Matches Tailwind's `desktop:` screen (tailwind.config.js). */
export const DESKTOP_QUERY = '(min-width: 1024px)';

const canMatch = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

export function useMediaQuery(query: string, fallback = true): boolean {
	const [matches, setMatches] = useState(() => (canMatch() ? window.matchMedia(query).matches : fallback));

	useEffect(() => {
		if (!canMatch()) return undefined;
		const mql = window.matchMedia(query);
		const update = () => setMatches(mql.matches);
		update();
		/* Safari < 14 only has the deprecated addListener API. */
		if (mql.addEventListener) {
			mql.addEventListener('change', update);
			return () => mql.removeEventListener('change', update);
		}
		mql.addListener(update);
		return () => mql.removeListener(update);
	}, [query]);

	return matches;
}

/** True at the `desktop:` breakpoint and wider. */
export const useIsDesktop = (): boolean => useMediaQuery(DESKTOP_QUERY);

export default useMediaQuery;
